/**
 * Enum representing the different user roles in the system
 */
export enum UserRole {
	ADMIN = "global_admin",
	ORG_ADMIN = "org_admin",
	RESPONDENT = "respondent",
}

export const UserRoleDescription: Record<UserRole, string> = {
	[UserRole.ADMIN]: "Access to all features",
	[UserRole.ORG_ADMIN]: "Manage organization details and associations",
	[UserRole.RESPONDENT]: "Access to survey features",
};

/**
 * Interface representing the roles a user has
 * Each role is a key with a boolean value
 */
export interface UserRoles {
	[key: string]: boolean;
}

/**
 * A user, as the admin app consumes one.
 *
 * Every field has exactly ONE spelling. This interface used to declare both
 * casings for six of them — `organizationId` and `organization_id`, `photoURL`
 * and `photo_url`, `displayName` and `display_name`, and the timestamp and
 * verification pairs — which is how `organizationId: "global"` typechecked for
 * years in `createGlobalAdmin` while `createUser` read `organization_id`. The
 * compiler could not tell "the field the schema uses" from "the field nothing
 * reads", so it flagged neither.
 *
 * Removing the duplicates immediately surfaced three live defects: two more
 * `organizationId` payloads, a `photoURL` emitted where every template reads
 * `photo_url`, and an `emailVerified` read off a Firestore document that is
 * always `undefined` because the stored field is `email_verified`.
 *
 * Names follow the Firestore document, per docs/standards.md, with one
 * deliberate exception: `displayName` is camelCase because it is not a stored
 * field at all. Firestore holds `first_name` / `last_name` only and the name is
 * derived at read time — see `deriveDisplayName` and the `syncAuthDisplayName`
 * trigger. Its camelCase spelling marks it as a view value rather than a column.
 */
export interface UserWithRoles {
	id?: string;
	uid?: string;
	email: string;
	/** Derived at read time, never stored. See the note above. */
	displayName?: string;
	photo_url?: string;
	roles: UserRoles | string[];
	active: boolean;
	/** Firebase Timestamp, mapped from the document's `last_sign_in_at`. */
	lastSignInAt?: any;
	email_verified?: boolean;
	first_name?: string;
	last_name?: string;
}
